import React, { useEffect, useMemo, useState } from 'react';
import api, { API_BASE_URL } from '../utils/api';

const ALL_SCOPES = [
  'tasks:read',
  'tasks:write',
  'projects:read',
  'projects:write',
  'clients:read',
  'clients:write',
  'comments:write',
  'search:read'
];

const ApiAccessSettings = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState(ALL_SCOPES);
  const [newKey, setNewKey] = useState('');
  const [error, setError] = useState('');
  const [editingKeyId, setEditingKeyId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editScopes, setEditScopes] = useState([]);

  const activeKeys = useMemo(() => keys.filter(k => k.active), [keys]);

  const loadKeys = async () => {
    try {
      setLoading(true);
      const res = await api.get('/settings/api-v1-keys');
      setKeys(res.data?.keys || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const toggleScope = (scope) => {
    setScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);
  };

  const createKey = async () => {
    if (!label.trim()) {
      setError('Label is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const res = await api.post('/settings/api-v1-keys', {
        label: label.trim(),
        scopes
      });
      setNewKey(res.data?.key || '');
      setLabel('');
      await loadKeys();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to create API key');
    } finally {
      setSaving(false);
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this API key? This cannot be undone.')) return;
    try {
      await api.delete(`/settings/api-v1-keys/${id}`);
      await loadKeys();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const rotateKey = async (id) => {
    if (!window.confirm('Rotate this API key now? The old key will be revoked immediately.')) return;
    try {
      setSaving(true);
      setError('');
      const res = await api.post(`/settings/api-v1-keys/${id}/rotate`);
      setNewKey(res.data?.key || '');
      await loadKeys();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to rotate API key');
    } finally {
      setSaving(false);
    }
  };

  const startEditKey = (key) => {
    setEditingKeyId(key.id);
    setEditLabel(key.label || '');
    setEditScopes(key.scopes || []);
    setError('');
  };

  const cancelEditKey = () => {
    setEditingKeyId(null);
    setEditLabel('');
    setEditScopes([]);
  };

  const toggleEditScope = (scope) => {
    setEditScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);
  };

  const saveKeyEdits = async () => {
    if (!editingKeyId) return;
    if (!editLabel.trim()) {
      setError('Label is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await api.patch(`/settings/api-v1-keys/${editingKeyId}`, {
        label: editLabel.trim(),
        scopes: editScopes,
      });
      cancelEditKey();
      await loadKeys();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to update API key');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">API &amp; MCP</h3>
        <p className="text-sm text-gray-600 mt-1">Connect agents and applications using Athena API v1 or the MCP adapter.</p>
      </div>

      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium">Connect an agent or application</h4>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
          <li>Create a key below with only the scopes your integration needs. Save it privately when it is shown.</li>
          <li>For MCP, install the adapter on your MCP client’s host and configure its launch command. Direct API clients do not need the adapter.</li>
          <li>Verify your identity and a task read before enabling writes. Follow the workbook for safe retries and updates.</li>
        </ol>
        <p className="text-sm text-gray-600">API origin: <code className="break-all">{API_BASE_URL.replace(/\/$/, '')}</code></p>
        <div className="flex flex-wrap gap-3 text-sm">
          <a className="px-3 py-2 bg-purple-600 text-white rounded" href={`${process.env.PUBLIC_URL || ''}/docs/athena-api-mcp-workbook.md`} download="athena-api-mcp-workbook.md">Download setup workbook (.md)</a>
          <a className="px-3 py-2 border rounded text-purple-700" href={`${API_BASE_URL.replace(/\/$/, '')}/docs`} target="_blank" rel="noopener noreferrer">Explore API contracts</a>
          <a className="px-3 py-2 border rounded text-purple-700" href={`${API_BASE_URL.replace(/\/$/, '')}/openapi.json`} target="_blank" rel="noopener noreferrer">OpenAPI JSON</a>
          <a className="px-3 py-2 border rounded text-purple-700" href="https://github.com/RLooney88/athena-mcp-server" target="_blank" rel="noopener noreferrer">MCP adapter &amp; source</a>
        </div>
        <p className="text-xs text-gray-500">Requires Node.js 22+, repository access, and a client supporting local stdio MCP. Core tools use API keys; six legacy tools require a user JWT. The workbook contains no credentials.</p>
      </div>

      {error && <div className="p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>}

      {newKey && (
        <div className="p-3 rounded bg-amber-50 border border-amber-200">
          <p className="text-sm font-medium text-amber-900">Copy this new API key now (shown once):</p>
          <code className="block mt-2 p-2 rounded bg-white border text-xs break-all">{newKey}</code>
        </div>
      )}

      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium">Create API Key</h4>
        <input
          className="w-full border rounded px-3 py-2"
          placeholder="Key label (e.g., OpenClaw Prod)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-2">
          {ALL_SCOPES.map(scope => (
            <label key={scope} className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggleScope(scope)} />
              <span>{scope}</span>
            </label>
          ))}
        </div>
        <button className="px-4 py-2 bg-purple-600 text-white rounded" onClick={createKey} disabled={saving}>
          {saving ? 'Creating...' : 'Create API Key'}
        </button>
      </div>

      <div className="border rounded-lg p-4">
        <h4 className="font-medium mb-3">Active Keys ({activeKeys.length})</h4>
        {loading ? (
          <p className="text-sm text-gray-500">Loading keys...</p>
        ) : activeKeys.length === 0 ? (
          <p className="text-sm text-gray-500">No active API keys yet.</p>
        ) : (
          <div className="space-y-2">
            {activeKeys.map(k => (
              <div key={k.id} className="border rounded p-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {editingKeyId === k.id ? (
                    <div className="space-y-2">
                      <input
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        placeholder="Key label"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        {ALL_SCOPES.map(scope => (
                          <label key={scope} className="text-xs flex items-center gap-2">
                            <input type="checkbox" checked={editScopes.includes(scope)} onChange={() => toggleEditScope(scope)} />
                            <span>{scope}</span>
                          </label>
                        ))}
                      </div>
                      <div className="flex items-center gap-3">
                        <button className="text-xs text-purple-700" onClick={saveKeyEdits} disabled={saving}>Save</button>
                        <button className="text-xs text-gray-600" onClick={cancelEditKey} disabled={saving}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="font-medium text-sm">{k.label}</div>
                      <div className="text-xs text-gray-500">{k.key_preview}</div>
                      <div className="text-xs text-gray-600 mt-1">Scopes: {(k.scopes || []).join(', ')}</div>
                    </>
                  )}
                </div>
                {editingKeyId !== k.id && (
                  <div className="flex flex-col items-end gap-2">
                    <button className="text-xs text-purple-700" onClick={() => startEditKey(k)}>Edit</button>
                    <button className="text-xs text-blue-700" onClick={() => rotateKey(k.id)} disabled={saving}>Rotate</button>
                    <button className="text-xs text-red-600" onClick={() => revokeKey(k.id)}>Revoke</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiAccessSettings;
